import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  BarChart3,
  Flag,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useReviewStats } from '@/features/review/hooks/useReview';
import type { ReviewPhase } from '@/features/review/types';

interface ScreeningDashboardProps {
  reviewId: string;
  totalSources: number;
  consecutiveExcludes: number;
  stopThreshold?: number;
  phase?: ReviewPhase;
  onFinish: () => void;
}

const PIE_COLORS = {
  Include: '#10b981',
  Maybe: '#f59e0b',
  Exclude: '#f43f5e',
  Remaining: '#e2e8f0',
};

export function ScreeningDashboard({
  reviewId,
  totalSources,
  consecutiveExcludes,
  stopThreshold = 20,
  phase = 'screening',
  onFinish,
}: ScreeningDashboardProps) {
  const { data: stats } = useReviewStats(reviewId, phase);

  const screened = stats?.screened ?? 0;
  const included = stats?.included ?? 0;
  const excluded = stats?.excluded ?? 0;
  const maybe = stats?.maybe ?? 0;
  const remaining = Math.max(0, totalSources - screened);
  const percentComplete = totalSources > 0 ? (screened / totalSources) * 100 : 0;

  const pieData = [
    { name: 'Include', value: included },
    { name: 'Maybe', value: maybe },
    { name: 'Exclude', value: excluded },
    ...(remaining > 0 ? [{ name: 'Remaining', value: remaining }] : []),
  ].filter((d) => d.value > 0);

  const safeToStop = consecutiveExcludes >= stopThreshold;

  return (
    <div className="space-y-4">
      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Total" value={totalSources} />
        <StatCard label="Screened" value={screened} />
        <StatCard label="Remaining" value={remaining} />
        <StatCard label="Complete" value={`${Math.round(percentComplete)}%`} />
      </div>

      {/* Decision counters */}
      <Card>
        <CardContent className="pt-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-slate-700">Include</span>
            </div>
            <span className="text-sm font-semibold text-emerald-700">{included}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <HelpCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-slate-700">Maybe</span>
            </div>
            <span className="text-sm font-semibold text-amber-700">{maybe}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <XCircle className="h-4 w-4 text-rose-500" />
              <span className="text-sm text-slate-700">Exclude</span>
            </div>
            <span className="text-sm font-semibold text-rose-700">{excluded}</span>
          </div>
        </CardContent>
      </Card>

      {/* Pie chart */}
      {pieData.length > 0 && (
        <Card>
          <CardHeader className="pb-0 pt-3">
            <CardTitle className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <BarChart3 className="h-3.5 w-3.5" />
              Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={65}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={PIE_COLORS[entry.name as keyof typeof PIE_COLORS] ?? PIE_COLORS.Remaining}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [`${value} sources`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Stopping indicator */}
      <Card>
        <CardHeader className="pb-2 pt-3">
          <CardTitle className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Flag className="h-3.5 w-3.5" />
            Stopping Indicator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>Consecutive excludes</span>
            <span className="font-medium">{consecutiveExcludes} / {stopThreshold}</span>
          </div>
          <Progress
            value={Math.min(100, (consecutiveExcludes / stopThreshold) * 100)}
            className="h-2"
          />
          {safeToStop && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
              <p className="text-xs font-medium text-emerald-700">
                Safe to stop — you&apos;ve likely found 95%+ of relevant sources
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Finish button */}
      <Button onClick={onFinish} className="w-full" variant="outline">
        Finish Screening
      </Button>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-center">
      <p className="text-lg font-bold text-slate-900">{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}
